/**
 * The document's bookmarks (outline): navigate, and edit as Acrobat does.
 *
 * Every change sends the whole tree back as one undo step. A bookmark keeps
 * the ``id`` it was read with, so the server keeps its colour and style.
 */

import { useEffect, useRef, useState } from 'react'
import type { Bookmark } from '../api'

type Path = number[]

interface Props {
  header: React.ReactNode
  bookmarks: Bookmark[]
  pageCount: number
  current: number
  readOnly: boolean
  onGoTo: (pageIndex: number, top: number | null) => void
  onOpenUri: (uri: string) => void
  onChange: (next: Bookmark[]) => Promise<boolean>
}

const clone = (tree: Bookmark[]): Bookmark[] => structuredClone(tree)

function at(tree: Bookmark[], path: Path): Bookmark | undefined {
  let list = tree
  let node: Bookmark | undefined
  for (const i of path) {
    node = list[i]
    if (!node) return undefined
    list = node.children
  }
  return node
}

function siblings(tree: Bookmark[], path: Path): Bookmark[] {
  return path.length === 1 ? tree : at(tree, path.slice(0, -1))!.children
}

const same = (a: Path | null, b: Path) => !!a && a.length === b.length && a.every((v, i) => v === b[i])

export function BookmarksPanel(props: Props) {
  const { bookmarks, readOnly } = props
  const [selected, setSelected] = useState<Path | null>(null)
  const [renaming, setRenaming] = useState<Path | null>(null)
  // Expanding is a view, not an edit (Acrobat does not mark the file changed
  // for it either): overrides of each bookmark's stored open state, by path.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const list = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (selected && !at(bookmarks, selected)) setSelected(null)
  }, [bookmarks, selected])
  useEffect(() => setExpanded({}), [bookmarks])
  const isOpen = (node: Bookmark, path: Path) => expanded[path.join('.')] ?? node.open

  const apply = async (work: (tree: Bookmark[]) => Path | null) => {
    if (readOnly) return
    const tree = clone(bookmarks)
    const next = work(tree)
    if (await props.onChange(tree)) setSelected(next)
    list.current?.focus()
  }

  const add = () => apply((tree) => {
    const title = window.prompt('Bookmark title:', `Page ${props.current + 1}`)
    if (!title?.trim()) return selected
    const node: Bookmark = { title: title.trim(), pageIndex: props.current, top: null, open: false, children: [] }
    if (selected) {
      const peers = siblings(tree, selected)
      peers.splice(selected[selected.length - 1] + 1, 0, node)
      return [...selected.slice(0, -1), selected[selected.length - 1] + 1]
    }
    tree.push(node)
    return [tree.length - 1]
  })

  const remove = (path: Path) => apply((tree) => {
    const node = at(tree, path)
    if (node?.children.length && !window.confirm(`Delete "${node.title}" and the ${node.children.length} bookmark(s) inside it?`)) {
      return path
    }
    siblings(tree, path).splice(path[path.length - 1], 1)
    return null
  })

  const rename = (path: Path, title: string) => {
    setRenaming(null)
    const node = at(bookmarks, path)
    if (!node || !title.trim() || title.trim() === node.title) return
    void apply((tree) => {
      at(tree, path)!.title = title.trim()
      return path
    })
  }

  const move = (path: Path, delta: -1 | 1) => apply((tree) => {
    const peers = siblings(tree, path)
    const i = path[path.length - 1]
    const j = i + delta
    if (j < 0 || j >= peers.length) return path
    ;[peers[i], peers[j]] = [peers[j], peers[i]]
    return [...path.slice(0, -1), j]
  })

  const indent = (path: Path) => apply((tree) => {
    const i = path[path.length - 1]
    if (i === 0) return path
    const peers = siblings(tree, path)
    const [node] = peers.splice(i, 1)
    const parent = peers[i - 1]
    parent.children.push(node)
    parent.open = true
    return [...path.slice(0, -1), i - 1, parent.children.length - 1]
  })

  const outdent = (path: Path) => apply((tree) => {
    if (path.length < 2) return path
    const parentPath = path.slice(0, -1)
    const [node] = siblings(tree, path).splice(path[path.length - 1], 1)
    const outer = siblings(tree, parentPath)
    const at_ = parentPath[parentPath.length - 1] + 1
    outer.splice(at_, 0, node)
    return [...parentPath.slice(0, -1), at_]
  })

  const setHere = (path: Path) => apply((tree) => {
    const node = at(tree, path)!
    node.pageIndex = props.current
    node.top = null
    node.uri = null
    return path
  })

  const toggle = (node: Bookmark, path: Path) =>
    setExpanded((previous) => ({ ...previous, [path.join('.')]: !isOpen(node, path) }))

  const go = (node: Bookmark) => {
    if (node.pageIndex !== null && node.pageIndex !== undefined) props.onGoTo(node.pageIndex, node.top ?? null)
    else if (node.uri) props.onOpenUri(node.uri)
  }

  const onKey = (event: React.KeyboardEvent) => {
    if (!selected || renaming) return
    const node = at(bookmarks, selected)
    if (!node) return
    const key = event.key
    if (key === 'Enter') go(node)
    else if (readOnly) return
    else if (key === 'F2') setRenaming(selected)
    else if (key === 'Delete') void remove(selected)
    else if (key === 'Tab' && event.shiftKey) void outdent(selected)
    else if (key === 'Tab') void indent(selected)
    else if (key === 'ArrowUp' && event.altKey) void move(selected, -1)
    else if (key === 'ArrowDown' && event.altKey) void move(selected, 1)
    else return
    event.preventDefault()
  }

  const rows = (nodes: Bookmark[], parent: Path): React.ReactNode[] =>
    nodes.flatMap((node, i) => {
      const path = [...parent, i]
      const where = node.pageIndex !== null && node.pageIndex !== undefined
        ? `Page ${node.pageIndex + 1}` : node.uri ?? 'Goes elsewhere'
      const row = (
        <div
          key={path.join('.')}
          className="bookmark"
          role="treeitem"
          aria-level={path.length}
          aria-expanded={node.children.length ? isOpen(node, path) : undefined}
          aria-selected={same(selected, path)}
          data-selected={same(selected, path) || undefined}
          style={{ paddingLeft: 4 + (path.length - 1) * 14 }}
          title={where}
          onClick={() => { setSelected(path); go(node) }}
          onDoubleClick={() => !readOnly && setRenaming(path)}
        >
          <button
            className="bookmark__twisty"
            aria-hidden={!node.children.length}
            tabIndex={-1}
            onClick={(event) => {
              event.stopPropagation()
              if (node.children.length) toggle(node, path)
            }}
          >
            {node.children.length ? (isOpen(node, path) ? '▾' : '▸') : ''}
          </button>
          {same(renaming, path) ? (
            <input
              className="bookmark__edit"
              defaultValue={node.title}
              autoFocus
              onClick={(event) => event.stopPropagation()}
              onBlur={(event) => rename(path, event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') rename(path, event.currentTarget.value)
                if (event.key === 'Escape') setRenaming(null)
                event.stopPropagation()
              }}
            />
          ) : (
            <span className="bookmark__title">{node.title}</span>
          )}
        </div>
      )
      return isOpen(node, path) ? [row, ...rows(node.children, path)] : [row]
    })

  const chosen = selected ? at(bookmarks, selected) : undefined
  return (
    <aside className="thumbs bookmarks" aria-label="Bookmarks">
      <div className="thumbs__header">
        {props.header}
        {!readOnly && (
          <div className="thumbs__actions">
            <button className="iconbtn" title="Add a bookmark for the current page" onClick={() => void add()}>+</button>
            <button className="iconbtn iconbtn--danger" title="Delete (Del)" disabled={!chosen}
                    onClick={() => selected && void remove(selected)}>{'✕'}</button>
          </div>
        )}
      </div>
      <div className="bookmarks__list" role="tree" tabIndex={0} ref={list} onKeyDown={onKey}>
        {bookmarks.length ? rows(bookmarks, []) : (
          <p className="bookmarks__empty">
            No bookmarks. {!readOnly && 'Go to a page and press + to add one.'}
          </p>
        )}
      </div>
      {chosen && !readOnly && selected && (
        <div className="bookmarks__tools">
          <button className="iconbtn" title="Rename (F2)" onClick={() => setRenaming(selected)}>{'✎'}</button>
          <button className="iconbtn" title="Move up (Alt+↑)" onClick={() => void move(selected, -1)}>{'↑'}</button>
          <button className="iconbtn" title="Move down (Alt+↓)" onClick={() => void move(selected, 1)}>{'↓'}</button>
          <button className="iconbtn" title="Nest under the one above (Tab)" onClick={() => void indent(selected)}>{'⇥'}</button>
          <button className="iconbtn" title="Un-nest (Shift+Tab)" onClick={() => void outdent(selected)}>{'⇤'}</button>
          <button className="iconbtn" title={`Point at page ${props.current + 1}, the one you are on`}
                  onClick={() => void setHere(selected)}>{'⌖'}</button>
        </div>
      )}
    </aside>
  )
}
